import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {jwtDecode} from 'jwt-decode';
import {firstValueFrom} from 'rxjs';
import {AdminService, AuthService} from '../openapi-core';
import {SessionService} from './session.service';

interface SchemeTokenClaims {
  role?: string | string[];
  scheme?: string;
}

const DASHBOARD_URL = '/general/dashboard';

/** Opens the scheme named by a shared link (Debug > Copy URL) before /load rehydrates the session. */
@Injectable({providedIn: 'root'})
export class SchemeLinkService {
  constructor(
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private adminService: AdminService,
    private sessionService: SessionService,
  ) {}

  /**
   * Switches to the linked scheme. An agent without access stays on their
   * current scheme and is sent to its dashboard instead of the linked page.
   */
  async open(schemeId: string): Promise<void> {
    // Without a destination /load merges its query params into the dashboard
    // URL, which would carry the link's param along and re-trigger it.
    if (!sessionStorage.getItem('destination')) {
      sessionStorage.setItem('destination', DASHBOARD_URL);
    }

    try {
      if (schemeId !== this.sessionService.schemeId) {
        await this.switchScheme(schemeId);
      }
    } catch {
      sessionStorage.setItem('destination', DASHBOARD_URL);
      this.snackBar.open('Unable to open this workspace. You may not have access to it.', undefined, {duration: 6000});
    }
  }

  private async switchScheme(schemeId: string): Promise<void> {
    const isSuperAdmin = this.sessionService.isSuperAdmin;
    const {token} = await firstValueFrom(isSuperAdmin
      ? this.authService.apiSuperAdminSchemeTokenPost(schemeId)
      : this.adminService.apiAdminSchemeSchemeIdTokenPost(schemeId));
    const claims = jwtDecode<SchemeTokenClaims>(token);
    const role = Array.isArray(claims.role) ? claims.role[0] : claims.role;

    // Also drops the previous scheme's cached details, so nothing shows them while /load runs.
    this.sessionService.removeScheme();
    this.sessionService.forgotCubeJsToken();
    this.sessionService.token = token;
    this.sessionService.schemeId = claims.scheme || schemeId;
    this.sessionService.agentRole = isSuperAdmin ? 'SuperAdmin' : role || this.sessionService.agentRole;
  }
}
